import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import swal from 'sweetalert2'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { useThunkDispatch } from '@campaignhub/react-hooks'

import { aiEditAsset, getAiEditStatus } from '@redux/modules/prompt'
import { loadAssets } from '@redux/modules/asset'
import { loadAiChildren } from '@redux/modules/aiLineage'

// CH-4304: generate a NEW asset from a base (the new-asset modal's AI tab). On completion the
// assets list reloads so the minted asset appears, and a swal offers to view the new asset or
// stay. Mirrors useAiVersionGeneration.
const AI_ASSET_INCLUDES = 'comments,file,histories,file.filePreviewRequests'
const AI_POLL_INTERVAL = 3000
const AI_POLL_TIMEOUT = 3 * 60 * 1000
const AI_POLL_MAX_FAILURES = 3

const useAiAssetGeneration = (serviceJobId, baseAssetId, options = {}) => {
  const { onViewAsset } = options

  const dispatch = useThunkDispatch()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [generating, setGenerating] = useState(false)
  const pollRef = useRef({ active: false, timeoutId: null })

  // CH-4304: 'View asset' leaves the modal and points the page at the minted asset.
  const redirectToAsset = (resultAssetId) => {
    const nextParams = new URLSearchParams(searchParams)
    nextParams.set('assetId', resultAssetId)
    navigate({ search: nextParams.toString() })
  }

  const stopPolling = () => {
    pollRef.current.active = false
    if (pollRef.current.timeoutId){
      clearTimeout(pollRef.current.timeoutId)
      pollRef.current.timeoutId = null
    }
  }

  const stopGenerating = () => {
    stopPolling()
    setGenerating(false)
  }

  const pollAiEdit = (requestId) => {
    stopPolling()
    pollRef.current.active = true
    const deadline = Date.now() + AI_POLL_TIMEOUT
    let consecutiveFailures = 0

    const tick = async () => {
      if (!pollRef.current.active) return

      const { success, data, status } = await dispatch(getAiEditStatus(requestId))
      if (!pollRef.current.active) return

      if (!success){
        if (status === 404){
          stopGenerating()
          toast.info('AI asset is generating. Please refresh the page shortly.')
          return
        }

        consecutiveFailures += 1
        if (consecutiveFailures >= AI_POLL_MAX_FAILURES){
          stopGenerating()
          toast.error('Couldn\'t check the generation status. Please refresh the page shortly.')
          return
        }

        pollRef.current.timeoutId = setTimeout(tick, AI_POLL_INTERVAL)
        return
      }

      consecutiveFailures = 0

      if (data?.status === 'Completed'){
        await dispatch(loadAssets({ serviceJobs: serviceJobId, includes: AI_ASSET_INCLUDES }))
        // CH-4304: refresh the base's child links too — the just-minted child must appear in
        // the Base row while the user stays (the children cache keys on the base id only).
        await dispatch(loadAiChildren(baseAssetId))
        if (!pollRef.current.active) return
        stopGenerating()

        const resultAssetId = data?.resultAssetId
        if (!resultAssetId){
          toast.success('AI asset generated — it now appears in the Assets tab.')
          return
        }

        swal.fire({
          title: 'AI Asset Generated',
          text: 'The new asset now appears in the Assets tab.',
          icon: 'success',
          showCancelButton: true,
          confirmButtonText: 'View Asset',
          cancelButtonText: 'Stay Here',
          showClass: 'slide-from-top',
        }).then(({ value }) => {
          if (!value) return
          onViewAsset?.(resultAssetId)
          redirectToAsset(resultAssetId)
        })
        return
      }

      if (data?.status === 'Failed'){
        stopGenerating()
        toast.error(data?.error || 'AI generation failed. Please try again.')
        return
      }

      if (Date.now() > deadline){
        stopGenerating()
        toast.error('AI generation is taking longer than expected. Please refresh the page shortly.')
        return
      }

      pollRef.current.timeoutId = setTimeout(tick, AI_POLL_INTERVAL)
    }

    tick()
  }

  const generate = async ({
    prompt, promptKey, customPrompt, outputName,
  }) => {
    const { success, data } = await dispatch(aiEditAsset(baseAssetId, prompt, promptKey, customPrompt, true, outputName))

    if (success){
      setGenerating(true)

      if (data?.requestId){
        pollAiEdit(data.requestId)
      } else {
        setGenerating(false)
        toast.info('AI asset is generating. Please refresh the page shortly.')
      }
    }

    return { success }
  }

  useEffect(() => () => stopPolling(), [])

  return { generating, generate, stopGenerating }
}

export default useAiAssetGeneration
